import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Clock, XCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/pages/LandingPage";
import { WHATSAPP_LINK, WhatsAppIcon } from "@/components/WhatsAppButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentResultPage() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [status, setStatus] = useState("CHECKING");
  const attempts = useRef(0);

  useEffect(() => {
    if (!orderId) { setStatus("INVALID"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/${orderId}`);
        setStatus(data.status || "UNKNOWN");
        if (data.status !== "PAID" && attempts.current < 10) {
          attempts.current += 1;
          timer = setTimeout(poll, 3000);
        }
      } catch {
        if (attempts.current < 10) {
          attempts.current += 1;
          timer = setTimeout(poll, 3000);
        } else {
          setStatus("UNKNOWN");
        }
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [orderId]);

  const paid = status === "PAID";
  const pending = status === "CHECKING" || status === "ACTIVE" || status === "UNKNOWN";

  return (
    <div className="min-h-screen bg-secondary/40 flex flex-col items-center justify-center px-6" data-testid="payment-result-page">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo /></div>
        <div className="rounded-3xl bg-white border border-black/5 shadow-[0_20px_40px_rgb(0,0,0,0.08)] p-10 text-center">
          {paid && (
            <>
              <span className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-5">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </span>
              <h1 className="font-display text-2xl font-semibold text-slate-900" data-testid="payment-success-title">Payment successful!</h1>
              <p className="mt-3 text-sm text-muted-foreground">
                Your virtual office order is confirmed. Our team will reach out within a few hours to start your setup.
              </p>
            </>
          )}
          {pending && (
            <>
              <span className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-5">
                <Clock className="w-8 h-8 text-amber-600 animate-pulse" />
              </span>
              <h1 className="font-display text-2xl font-semibold text-slate-900" data-testid="payment-pending-title">Confirming your payment...</h1>
              <p className="mt-3 text-sm text-muted-foreground">This takes a few seconds. Don't close this page.</p>
            </>
          )}
          {!paid && !pending && (
            <>
              <span className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-5">
                <XCircle className="w-8 h-8 text-red-500" />
              </span>
              <h1 className="font-display text-2xl font-semibold text-slate-900" data-testid="payment-failed-title">Payment not completed</h1>
              <p className="mt-3 text-sm text-muted-foreground">No money was charged for incomplete payments. You can try again.</p>
              <Link to="/checkout">
                <Button data-testid="payment-retry-button" className="mt-6 w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-white font-semibold">
                  Try Again <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </>
          )}
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
            className="mt-6 flex items-center justify-center gap-2 text-sm text-[#25D366] hover:underline" data-testid="payment-whatsapp-link">
            <WhatsAppIcon className="w-4 h-4" /> Questions? Chat with us
          </a>
        </div>
      </div>
    </div>
  );
}
